import { ColumnsType, defaultTheme, Text } from '@agro-club/agroclub-shared'
import { useMemo } from 'react'
import * as renderers from 'views/components/Table/renderers'
import { useTranslation } from 'react-i18next'
import { RailwayRoute } from '../../types'
import { rubPerTon } from 'modules/utils/numbers/formatTarif'
import { RailsRatesActionCell } from './RailsRatesActionCell'
import { TruncatedValue } from 'views/components/TruncatedValue/TruncatedValue'
import { convertSortingToAntDFormat } from 'modules/domain/common/tableUtils'
import { ListRequestParams } from 'modules/domain/types'
import { RateCell } from '../RateCell'
import { DateFormatted } from 'views/components/DateFormatted'
import { parseISO } from 'date-fns'

const stationCell = (station) => (
  <div>
    <div>
      {station.title} ({station.station_code})
    </div>
    <Text color="secondary" typography="bodySmall">
      {station.railway_region}
    </Text>
  </div>
)
const useRailsColumns = (onSuccess: () => void, sortParam: ListRequestParams['sort']): ColumnsType<RailwayRoute> => {
  const { t } = useTranslation('rates')

  return useMemo(
    () => [
      {
        title: t('fromStation'),
        dataIndex: 'from_station',
        width: 300,
        sorter: true,
        sortOrder: convertSortingToAntDFormat('from_station', sortParam),
        render: stationCell,
      },
      {
        title: t('toStation'),
        dataIndex: 'to_station',
        width: 300,
        sorter: true,
        sortOrder: convertSortingToAntDFormat('to_station', sortParam),
        render: stationCell,
      },
      {
        title: t('common:product'),
        dataIndex: 'product',
        width: 140,
        render: (product) => product?.name,
      },
      {
        title: rubPerTon(t('rate')),
        dataIndex: 'rate',
        align: 'right',
        width: 110,
        render: (_, record) => <RateCell rate={record} />,
        onCell: (record) => {
          const rate = record
          let cellColor: string | undefined
          if (rate?.history_info) {
            if ((rate.rate || 0) > (rate.history_info.rate || 0)) {
              cellColor = defaultTheme.color.accentDestruct16a
            } else if ((rate.rate || 0) < (rate.history_info.rate || 0)) {
              cellColor = defaultTheme.color.accentApprove16a
            }
          }
          return { style: { backgroundColor: cellColor } }
        },
      },
      {
        title: t('start_date'),
        width: 110,
        dataIndex: 'start_date',
        render: renderers.date,
      },
      {
        title: t('end_date'),
        width: 110,
        dataIndex: 'end_date',
        render: (date) => {
          if (!date) return null
          const parseDate = parseISO(date)
          return (
            <Text typography="bodyMedium" color={parseDate < new Date() ? 'accentDestruct600' : undefined}>
              <DateFormatted date={date} />
            </Text>
          )
        },
      },
      {
        title: t('rail_car_type'),
        width: 100,
        dataIndex: 'rail_car_type',
        render: (rail_car_type) => (rail_car_type ? t(`railCarTypes.${rail_car_type}`) : null),
      },
      {
        title: t('rail_car_owner_short'),
        width: 120,
        dataIndex: 'rail_car_owner',
        render: (rail_car_owner) => rail_car_owner?.name,
      },
      {
        title: t('rail_car_load_limit_short'),
        width: 110,
        align: 'right',
        dataIndex: 'rail_car_load_limit',
      },
      {
        title: t('common:comment'),
        width: 300,
        dataIndex: 'comment',
        render: (comment) => <TruncatedValue hint={comment}>{comment}</TruncatedValue>,
      },
      {
        title: t('common:modified'),
        width: 100,
        dataIndex: 'modified',
        render: renderers.date,
      },
      {
        title: '',
        width: 100,
        dataIndex: 'id',
        align: 'center',
        fixed: 'right',
        render: (id, data) => <RailsRatesActionCell onSuccess={onSuccess} routeData={data} />,
      },
    ],
    [onSuccess, sortParam, t],
  )
}

export default useRailsColumns
